import { TextCountdown } from '@/components/_event/general/text-countdown'
import { Button } from '@/components/ui/button'
import { timezoneDateTime } from '@/helpers/datetime'
import Link from 'next/link'
import { useCallback } from 'react'

export default function ChampionshipEventCoundown({ event }) {
  const getCountdownData = useCallback(() => {
    if (!event.startDateTime) {
      return {
        value: 'unscheduled',
        text: 'Schedule this event before publishing race timing.',
        backgroundColor: 'bg-transparent',
        borderColor: 'border-error-500',
        textColor: 'text-error-500',
        useButton: false,
        targetTime: null,
      }
    }

    const eventHasStarted = new Date(event.startDateTime) < new Date()

    if (!eventHasStarted) {
      return {
        value: 'upcoming',
        text: 'Event starts in',
        backgroundColor: 'bg-transparent',
        borderColor: 'border-success-500',
        textColor: 'text-success-500',
        useButton: false,
        targetTime: timezoneDateTime({
          datetime: event.startDateTime,
          timezone: event.targetTimezone,
          format: false,
        }),
      }
    }

    const eventIsLive =
      eventHasStarted &&
      event.finishDateTime &&
      new Date(event.finishDateTime) > new Date()

    if (eventIsLive) {
      return {
        value: 'live',
        text: '',
        backgroundColor: '',
        borderColor: 'border-none',
        textColor: '',
        useButton: false,
        targetTime: null,
      }
    }

    const eventResultsPublished = event.sessions.every(
      (session) => session.resultsPublishedTs,
    )

    if (eventResultsPublished) {
      return {
        value: 'published',
        text: 'Event Completed & Results Assigned',
        useButton: true,
        buttonText: 'View Results',
        buttonBorderColor: '',
        buttonHoverColor: 'hover:bg-success-300!',
        buttonLink: `/manage/${event.championship.communityId}/racing/${event.championshipId}/results?eventId=${event.id}`,
        backgroundColor: 'bg-success-500',
        borderColor: 'border-success-500',
        textColor: 'text-shades-100',
        targetTime: null,
      }
    }

    return {
      value: 'finished',
      text: 'This event has finished',
      useButton: true,
      buttonText: 'Assign Results',
      buttonBorderColor: 'border-warning-500',
      buttonHoverColor: 'hover:bg-neutral-blue-700!',
      buttonLink: `/manage/${event.championship.communityId}/racing/${event.championshipId}/results/${event.id}`,
      backgroundColor: 'bg-transparent',
      borderColor: 'border-warning-500',
      textColor: 'text-shades-0',
      targetTime: null,
    }
  }, [event])

  return (
    <div
      className={`flex min-h-16 flex-col items-center justify-center gap-1 rounded-sm border p-4 text-center text-p-1 font-bold sm:flex-row ${getCountdownData().backgroundColor} ${getCountdownData().borderColor} ${getCountdownData().textColor}`}
    >
      {getCountdownData().value === 'live' ? (
        <div className="relative text-center text-h-4 font-bold uppercase">
          <p className="absolute font-syncopate text-success">Live</p>
          <p className="animate-[ping_2s_cubic-bezier(0,0,0.2,2)_infinite] font-syncopate uppercase text-success">
            Live
          </p>
        </div>
      ) : (
        <>
          <p>{getCountdownData().text}</p>

          {getCountdownData().targetTime && (
            <>
              <TextCountdown
                targetDate={getCountdownData().targetTime}
                className="text-nowrap"
              />
              <span className="hidden md:inline-block">• Good to race!</span>
            </>
          )}

          {getCountdownData().useButton && (
            <Button
              variant="outline"
              size="sm"
              className={`ml-2 font-semibold ${getCountdownData().buttonHoverColor} !${getCountdownData().buttonBorderColor} !${getCountdownData().textColor} bg-transparent!`}
              asChild
            >
              <Link href={getCountdownData().buttonLink}>
                {getCountdownData().buttonText}
              </Link>
            </Button>
          )}
        </>
      )}
    </div>
  )
}
